import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownTitle,
} from '@/components/Dropdown'
import { match } from '@/utils/pattern-match-util'
import { IconButton } from '@/components/IconButton'
import { pluralize } from '@/utils/pluralization-util'

export interface MoreMenuProps {
  canNotifyPros: boolean
  onNotifyPros: () => void
  selectedCount: number
  /**
   * `null` when the current selection can be neither archived nor restored.
   */
  archiveRestoreAction: 'Archive' | 'Restore' | null
  onArchiveRestore: () => void
  canClone: boolean
  onClone: () => void
  canTransfer: boolean
  onTransfer: () => void
  canAdvance: boolean
  onAdvance: () => void
}

/**
 * Admin-only actions. Hidden entirely when there is nothing to offer, which
 * is always the case for pros and clients.
 */
export function MoreMenu({
  canNotifyPros,
  onNotifyPros,
  selectedCount,
  archiveRestoreAction,
  onArchiveRestore,
  canClone,
  onClone,
  canTransfer,
  onTransfer,
  canAdvance,
  onAdvance,
}: MoreMenuProps) {
  const hasSelection = selectedCount > 0

  if (!canNotifyPros && !hasSelection) {
    return null
  }

  return (
    <Dropdown
      autoDismiss
      position="bottom-end"
      testId="job-management-table-menu-toggle"
      button={<IconButton name="more" label="More" size={16} />}
    >
      {canNotifyPros && (
        <DropdownItem
          onPress={onNotifyPros}
          buttonTestId="job-management-table-menu-notify-pros-unclaimed"
        >
          Notify pros of unclaimed jobs
        </DropdownItem>
      )}
      {hasSelection && (
        <>
          {canNotifyPros && <DropdownDivider />}
          <DropdownTitle>{selectedCount} selected</DropdownTitle>
          {archiveRestoreAction && (
            <DropdownItem
              onPress={onArchiveRestore}
              buttonTestId="job-management-table-menu-archive-restore-jobs"
            >
              {match(archiveRestoreAction, {
                Restore: () => 'Restore archived jobs',
                Archive: () => 'Archive jobs',
              })}
            </DropdownItem>
          )}
          {canClone && (
            <DropdownItem
              onPress={onClone}
              buttonTestId="job-management-table-menu-clone-jobs"
            >
              Clone jobs
            </DropdownItem>
          )}
          {canTransfer && (
            <DropdownItem
              onPress={onTransfer}
              buttonTestId="job-management-table-menu-bulk-transfer-jobs"
            >
              Transfer {selectedCount > 1 ? 'jobs' : 'job'}
            </DropdownItem>
          )}
          {canAdvance && (
            <DropdownItem
              onPress={onAdvance}
              buttonTestId="job-management-table-menu-advance-jobs"
            >
              Advance {selectedCount} {pluralize('job', selectedCount)}
            </DropdownItem>
          )}
        </>
      )}
    </Dropdown>
  )
}
